import { Truck, Package, Clock, Zap, CheckCircle, ArrowRight, Shield, Award, Users } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function ServicesPage() {
  const services = [
    {
      icon: Truck,
      title: 'LTL Shipments',
      description: 'Cost-effective freight solutions for shipments that don\'t require a full truckload. Flexible scheduling and competitive rates for businesses of all sizes.',
      features: [
        'Palletized Cargo Handling',
        'Real-time Tracking Included',
        'Secure Handling & Transit',
        'Flexible Pickup Scheduling',
        'Volume Discounts Available',
        'Insurance Options'
      ],
      badge: 'Most Popular',
      pricing: 'From $0.50/lb'
    },
    {
      icon: Zap,
      title: 'Same-Day Courier',
      description: 'Urgent deliveries handled with speed and precision. Perfect for time-sensitive documents and packages across the Greater Toronto Area.',
      features: [
        '2-4 Hour Delivery Window',
        'Real-time GPS Tracking',
        'Direct Route Planning',
        'Priority Handling',
        'Signature Confirmation',
        'Evening Delivery Available'
      ],
      badge: 'Express',
      pricing: 'From $25 per delivery'
    },
    {
      icon: Clock,
      title: 'Scheduled Delivery',
      description: 'Plan ahead with our reliable scheduled delivery services. Choose your pickup and delivery times that work best for your business operations.',
      features: [
        'Flexible Timing Options',
        'Route Optimization',
        'Regular Service Routes',
        'Volume Discounts',
        'Recurring Schedules',
        'Account Management'
      ],
      badge: null,
      pricing: 'Custom Pricing'
    },
    {
      icon: Package,
      title: 'Freight Transport',
      description: 'Full truckload services for larger shipments. Dedicated vehicles and experienced drivers ensure safe transport of your valuable cargo.',
      features: [
        'Dedicated Fleet Access',
        'Heavy Cargo Capable',
        'Long Distance Service',
        'Comprehensive Insurance',
        'Climate Controlled Options',
        'White Glove Service'
      ],
      badge: null,
      pricing: 'Request Quote'
    }
  ];

  const additionalServices = [
    {
      icon: Shield,
      title: 'White Glove Service',
      description: 'Premium handling with extra care for fragile or high-value items. Our specialized team ensures your delicate cargo receives VIP treatment.'
    },
    {
      icon: Package,
      title: 'Warehousing',
      description: 'Short-term and long-term storage solutions with 24/7 security. Climate-controlled facilities available for sensitive items.'
    },
    {
      icon: Truck,
      title: 'Cross-Border',
      description: 'Canada-US shipping with full customs clearance assistance. Streamlined documentation and compliance handling.'
    },
    {
      icon: Zap,
      title: 'Rush Delivery',
      description: 'Express service for critical shipments under tight deadlines. Dedicated vehicles and priority routing for urgent needs.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section with Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHRydWNrfGVufDB8fHx8MTc3ODEwODIwNnww&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Our Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Complete <span className="text-gradient">Logistics Solutions</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              From same-day courier to full freight transport, we provide comprehensive delivery services tailored to your business needs across GTA and Ontario. Our fleet of modern vehicles and experienced drivers ensure your shipments arrive safely and on time.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            {[
              { icon: Truck, value: '500+', label: 'Monthly Deliveries' },
              { icon: Clock, value: '2-4h', label: 'Same-Day Service' },
              { icon: Shield, value: '$2M', label: 'Insurance Coverage' },
              { icon: Award, value: '99.5%', label: 'On-Time Rate' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="p-4 bg-card/80 backdrop-blur-sm border-border/50 text-center">
                  <div className="p-2 rounded-lg bg-primary/10 inline-flex mb-2">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-gradient">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="scroll-animate bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardHeader className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="p-4 rounded-xl bg-gradient-primary group-hover:shadow-glow transition-all duration-300">
                        <Icon className="h-8 w-8 text-primary-foreground" />
                      </div>
                      {service.badge && (
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                          {service.badge}
                        </Badge>
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {service.description}
                      </p>
                      <div className="text-lg font-semibold text-primary">
                        {service.pricing}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="font-semibold mb-3">Service Features:</div>
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Image Showcase */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate">
              <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
                Our Fleet
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Modern <span className="text-gradient">Vehicles & Equipment</span>
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Our fleet consists of modern, well-maintained vehicles equipped with the latest technology. From cargo vans for local deliveries to full-size trucks for freight transport, we have the right vehicle for every shipment size.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 mt-1">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">GPS Tracked Fleet</div>
                    <div className="text-sm text-muted-foreground">Real-time location monitoring for all vehicles</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 mt-1">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Regular Maintenance</div>
                    <div className="text-sm text-muted-foreground">All vehicles serviced on schedule for reliability</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 mt-1">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Professional Drivers</div>
                    <div className="text-sm text-muted-foreground">Licensed, trained, and background-checked personnel</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="scroll-animate" style={{ animationDelay: '0.15s' }}>
              <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
                <img 
                  src="https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMHRydWNrfGVufDB8fHx8MTc3ODEwODIwNnww&ixlib=rb-4.1.0&q=85"
                  alt="Syncline Logistics Fleet"
                  className="w-full h-auto"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Additional <span className="text-gradient">Services</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Specialized services to meet your unique logistics needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4 group-hover:shadow-glow transition-all">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h4 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">{service.title}</h4>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </Card>
              );
            })}
          </div>

          {/* Warehouse Image */}
          <div className="scroll-animate">
            <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
              <img 
                src="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2V8ZW58MHx8fHwxNzc4MTA4MjA2fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern Warehouse Facility"
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">State-of-the-Art Facilities</h3>
                <p className="text-muted-foreground">
                  Our modern warehouse facilities feature advanced security systems, climate control, and efficient loading docks to handle your cargo with care.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate p-12 bg-gradient-card backdrop-blur-sm border-primary/20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Need a <span className="text-gradient">Custom Solution?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Contact us to discuss your unique logistics requirements. We'll create a tailored solution that fits your business perfectly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg px-8 w-full sm:w-auto">
                  Request a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:(647)-642-0062">
                <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 text-lg px-8 w-full sm:w-auto">
                  Call: (647)-642-0062
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
